import { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useDiffitUserContext } from '../contexts/DiffitUserContext';
import { resolvePlanTier } from './planTier';

// Pure classifier for the export freemium treatment. The plan tier
// (free/trial/paid, or 'unknown' until LD identifies) comes from the shared
// resolvePlanTier — the same primitive the My Resources gate uses.
export const resolveExportGate = ({ isLDidentified, isFreeTrialUser, hasActiveSubscription }) => {
  const tier = resolvePlanTier({ isLDidentified, hasActiveSubscription, isFreeTrialUser });
  const on = tier !== 'unknown';
  return {
    tier,
    // gateActive = the freemium treatment governs crowns on this surface. When
    // false (not yet identified) surfaces keep their legacy behavior.
    gateActive: on,
    showTrialCountdown: on && tier === 'trial',
    showPremiumCrowns: on && (tier === 'trial' || tier === 'free'),
  };
};

// ANCHOR: export-trial-copy — single source of truth for the trial countdown
// wording. No "unlimited access"; "premium trial" + "See Plans" (matches the
// resource gate). `emphasis` is the clause rendered in font-semibold.
export const getTrialCountdownParts = (daysLeftInTrial, diffitUser) => {
  if (daysLeftInTrial === 0) {
    return { lead: 'Your premium trial ends ', emphasis: 'today' };
  }
  if (daysLeftInTrial > 0) {
    const unit = daysLeftInTrial === 1 ? 'day' : 'days';
    return { lead: 'Your premium trial ends ', emphasis: `in ${daysLeftInTrial} ${unit}` };
  }
  const iso = diffitUser?.trial_end_date_iso;
  if (iso) {
    return {
      lead: 'Your premium trial ends ',
      emphasis: `on ${format(new Date(iso), 'MMMM do, yyyy')}`,
    };
  }
  return { lead: 'Your premium trial is active', emphasis: null };
};

export const getTrialCountdownLabel = (daysLeftInTrial, diffitUser) => {
  const { lead, emphasis } = getTrialCountdownParts(daysLeftInTrial, diffitUser);
  return emphasis ? `${lead}${emphasis}.` : `${lead}.`;
};

export const useExportGate = () => {
  const { isLDidentified, isFreeTrialUser, hasActiveSubscription, daysLeftInTrial, diffitUser } =
    useDiffitUserContext();
  const gate = resolveExportGate({
    isLDidentified,
    isFreeTrialUser,
    hasActiveSubscription,
  });
  return { ...gate, daysLeftInTrial, diffitUser };
};

// Fires the "viewed" event once per mount when the trial banner shows. Takes
// the surface's own track fn so this module stays free of Mixpanel imports
// (keeps the pure exports unit-testable in node). `show` is already
// identified-gated by resolveExportGate.
export const useTrialBannerViewed = (trackFn, show, location) => {
  const { daysLeftInTrial } = useDiffitUserContext();
  const firedRef = useRef(false);
  useEffect(() => {
    if (show && !firedRef.current && typeof trackFn === 'function') {
      firedRef.current = true;
      trackFn('Export Trial Banner Viewed', { location, days_left: daysLeftInTrial });
    }
  }, [trackFn, show, location, daysLeftInTrial]);
};
